import React from "react";
import NotificationMenu from "./NotificationMenu";
import { Building2, Menu } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetDataApiCache } from "../../app/apiCache";
import ActionButton from "../ui/ActionButton";
import PaginatedSelect from "../ui/PaginatedSelect";
import { usePlantOptions } from "../../features/plants/usePlantOptions";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import {
  clearAuthSession,
  getStoredUserDetails,
  notifyAuthSessionChanged,
} from "../../services/authSession";

const getInitials = (value) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "UN";

const Navbar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = getStoredUserDetails() || {};
  const { selectedPlantId, setSelectedPlantId } = usePlantSelection();
  const { options: plantOptions, isFetching: isLoadingPlants } = usePlantOptions();

  const displayName =
    userDetails.name ||
    `${userDetails.firstName || ""} ${userDetails.lastName || ""}`.trim() ||
    "User";
  const tenantName = userDetails.tenantName || "N/A";

  const handleLogout = () => {
    resetDataApiCache(dispatch);
    clearAuthSession();
    notifyAuthSessionChanged();
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-100 bg-white px-3 py-3 shadow-[0_2px_8px_rgba(15,23,42,0.06)] md:px-4">
      <div className="flex shrink-0 items-center gap-1 sm:gap-3">
        <button
          type="button"
          onClick={toggleSidebar}
          className="rounded-md p-2 text-darkGray hover:bg-lightPrimary hover:text-primary"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>
        <img
          src={`${process.env.PUBLIC_URL}/jbm-logo.svg`}
          alt="JBM Logo"
          className="w-12 sm:w-20 md:w-24"
        />
      </div>

      <div className="flex min-w-0 items-center gap-2 md:gap-4">
        <div className="w-28 sm:w-52 lg:w-64">
          <PaginatedSelect
            value={selectedPlantId}
            onChange={setSelectedPlantId}
            options={plantOptions}
            disabled={isLoadingPlants && plantOptions.length === 0}
            placeholder={isLoadingPlants ? "Loading plants..." : "Select plant"}
            icon={Building2}
            buttonClassName="border-slate-200 bg-slate-50"
          />
        </div>
        <NotificationMenu />
        <div className="flex items-center gap-2">
          <p className="flex h-9 w-9 items-center justify-center rounded-full bg-lightPrimary text-sm font-medium text-primary">
            {getInitials(displayName || userDetails.email || "User")}
          </p>

          <div className="hidden md:block">
            <p className="text-sm leading-tight">{displayName}</p>
            <p className="text-xs leading-tight text-gray-500">
              Tenant: {tenantName}
            </p>
          </div>
        </div>

        <ActionButton
          action="logout"
          onClick={handleLogout}
          className="hidden text-white hover:bg-primary/90 sm:inline-flex"
          aria-label="Logout"
        />
      </div>
    </header>
  );
};

export default Navbar;
